import React, { useState, useContext } from "react";
import { Card, Col, Row, Table, Form, Modal, Button, Spinner} from "react-bootstrap";
import { Helmet } from 'react-helmet-async';
import NotyfContext from "../../contexts/NotyfContext";
import { UserSettingsContext } from "../../contexts/UserSettingsContext";
import authenticatedFetch from "../../utils/fetch"
import LastRun from "../../utils/LastRun";
import ReportFormatSelect from "../../utils/ReportFormatSelect";
import TaskPracticeSelect from "../../utils/TaskPracticeSelect";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileCsv,
} from "@fortawesome/free-solid-svg-icons";


const ProviderRosterModal = ({ show, onClose, runSuccess}) => {

  const [practiceIds, setPracticeIds] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [reportFormat, setReportFormat] = useState("CSV");
  const [isExporting, setIsExporting] = useState(false);

  const notyf = useContext(NotyfContext);

  const handlePracticeChanged = (practices) => {
      setSelectedPractices(practices);
      setPracticeIds(practices.map((practice) => practice.value));
  }

  const handleReportFormatChanged = (value) => {
    setReportFormat(value);
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleExportClicked = (e) => {
      e.preventDefault();
      setIsExporting(true);
  
      authenticatedFetch({
        method: "POST",
        path:  "report/providerroster",
        additionalHeaders: {
          "X-Timezone": timezone,
        },
        requestData: {
          practiceIds: practiceIds,
          includeInactive: includeInactive,
          reportFormat: reportFormat,
        },
        successHandler: function(result) {
          window.open(result.url);
          setIsExporting(false);

          runSuccess("providerroster")
          onClose();
        },
        errorHandler: function(error) {
          setIsExporting(false);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
  }
  
  return (
      <Modal show={show} onHide={onClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Configure Report | Provider Roster</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                  <td>Practice(s):</td>
                  <td><div style={{width: "350px"}}><TaskPracticeSelect handlePracticesChanged={handlePracticeChanged} selectedPractices={selectedPractices} messagePrefix="Select"/></div></td>
              </tr>
              <tr>
                <td>&nbsp;</td>
                <td><Form.Check type="checkbox" inline name="group1" label="Include Inactive Providers" checked={includeInactive} onChange={(e) => {setIncludeInactive(e.target.checked)}} /></td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
        {isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '15px', marginTop: '11px', marginBottom: '11px'}}/>
          :
          <>
              <ReportFormatSelect selectedReportFormat={reportFormat} handleReportFormatChanged={handleReportFormatChanged} />
              <Button onClick={onClose}>Close</Button>
              <Button onClick={(e) => {handleExportClicked(e)}}>Run Report</Button>
          </>
          }
        </Modal.Footer>
      </Modal>
  );
}

const SupervisingPhysiciansModal = ({ show, onClose, runSuccess}) => {

  const [practiceIds, setPracticeIds] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [reportFormat, setReportFormat] = useState("PDF");
  const [isExporting, setIsExporting] = useState(false);

  const notyf = useContext(NotyfContext);

  const handlePracticeChanged = (practices) => {
      setSelectedPractices(practices);
      setPracticeIds(practices.map((practice) => practice.value));
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleExportClicked = (e) => {
      e.preventDefault();
      setIsExporting(true);
  
      authenticatedFetch({
        method: "POST",
        path:  "report/supervisedproviders",
        requestData: {
          practiceIds: practiceIds,
          reportFormat: reportFormat,
        },
        additionalHeaders: {
          "X-Timezone": timezone,
        },
        successHandler: function(result) {
          window.open(result.url);
          setIsExporting(false);

          runSuccess("supervisedproviders")
          onClose();
        },
        errorHandler: function(error) {
          setIsExporting(false);
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
  }
  
  return (
      <Modal show={show} onHide={onClose}>
        <Modal.Header closeButton style={{backgroundColor: '#DEF2F1'}}>
          <Modal.Title>
            <span style={{fontSize: "0.9em", fontWeight: "normal"}}>Configure Report | Supervised Providers</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table> 
            <tbody>
              <tr>
                  <td>Practice(s):</td>
                  <td><div style={{width: "350px"}}><TaskPracticeSelect handlePracticesChanged={handlePracticeChanged} selectedPractices={selectedPractices} messagePrefix="Select"/></div></td>
              </tr>
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer style={{backgroundColor: '#DEF2F1'}}>
        {isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '15px', marginTop: '11px', marginBottom: '11px'}}/>
          :
          <>
              <Button onClick={onClose}>Close</Button>
              <Button onClick={(e) => {handleExportClicked(e)}}>Run Report</Button>
          </>
          }
        </Modal.Footer>
      </Modal>
  );
}

const ReportList = () => {
    const [showProviderRosterModal, setShowProviderRosterModal] = useState(false);
    const [showSupervisingPhysiciansModal, setShowSupervisingPhysiciansModal] = useState(false);

    const [lastRunRefresh, setLastRunRefresh] = useState(0);

    const { isH3Admin, authorities } = useContext(UserSettingsContext);

    const runSuccess = (e) => {
      setLastRunRefresh(lastRunRefresh + 1);
    }

    return (
      <Card>
        <Card.Header style={{marginBottom: '-15px'}}>
            <Card.Title>
            Practice Reports
            </Card.Title>
          </Card.Header>
        <Table striped hover>
          <thead>
            <tr>
              <td>Report Type</td>
              <td>Available Formats</td>
              <td>Last Run</td>
            </tr>
          </thead>
          <tbody>
            {(isH3Admin || authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT")) ?
            <tr style={{cursor:"pointer"}} onClick={(e) => {setShowProviderRosterModal(true)}}>
                <td><a href="#">Provider Roster</a></td>
                <td><FontAwesomeIcon icon={faFileCsv} size="lg" style={{color: '#2B7A78'}}/> | <FontAwesomeIcon icon={faFilePdf} size="lg" style={{color: '#2B7A78'}}/></td>
                <td><LastRun reportType="providerroster" refresh={lastRunRefresh}/></td>
            </tr>
             : <></>
            }
            {(isH3Admin || authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT")) ?
            <tr style={{cursor:"pointer"}} onClick={(e) => {setShowSupervisingPhysiciansModal(true)}}>
                <td><a href="#" onClick={(e) => e.preventDefault()}>Supervised Providers</a></td>
                <td><FontAwesomeIcon icon={faFilePdf} size="lg" style={{color: '#2B7A78'}}/></td>
                <td><LastRun reportType="supervisedproviders" refresh={lastRunRefresh}/></td>
            </tr>
             : <></>
            }
          </tbody>
        </Table>
        <ProviderRosterModal show={showProviderRosterModal} onClose={() => setShowProviderRosterModal(false)} runSuccess={runSuccess} />
        <SupervisingPhysiciansModal show={showSupervisingPhysiciansModal} onClose={() => setShowSupervisingPhysiciansModal(false)} runSuccess={runSuccess} />
      </Card>
    )
  };

const PracticeReports = () => {

    return (
      <Row>
        <Helmet title="Practice Reports"/>
        <Col lg="12">
          <ReportList />
        </Col>
      </Row>
    )
  };
  
  export default PracticeReports;
  